import {
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import NoResultsRow from '../components/NoResultsRow';
import TableLoading from '../components/TableLoading';
import useTable from '../hooks/useTable';
import { useAuditLogSearchQuery, PaginatedAuditLogResponse } from '../queries';

import theme from '../theme';
import dayjs from 'dayjs';
import { useDebounce } from 'use-debounce';
import AuditLogActions, { getActionName } from '../constants/AuditLogActions';
import AuditLogFilter, { AuditLogSearchFilter } from '../components/audit-log/AuditLogFilter';

const useStyles = makeStyles({
  activeStatus: {
    color: theme.palette.primary.main,
  },
  disabledStatus: {
    color: theme.palette.error.main,
  },
  buttonContainer: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
});

export default function AuditLog() {
  const { page, pageSize, sortField, sortDirection, pageChanged, pageSizeChanged, sort } = useTable(
    'createdAt',
    undefined,
    undefined,
    'desc'
  );
  const [filter, setFilter] = useState<AuditLogSearchFilter>({});

  const { loading: rawLoading, error, data, refetch } = useAuditLogSearchQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      skip: pageSize * page,
      take: pageSize,
      sortDirection,
      sortField,
      from: filter.fromDate,
      to: filter.toDate,
      action: filter.action,
      textCriteria: filter.textCriteria,
    },
  });

  const [loading] = useDebounce(rawLoading, 300); // this is to prevent calling the api with every key stroke in the text input.
  const classes = useStyles();

  const [pagedResult, setPagedResult] = useState<PaginatedAuditLogResponse | undefined>();

  useEffect(() => {
    if (data) {
      setPagedResult(data?.auditLogs as PaginatedAuditLogResponse);
    }
  }, [data]);

  useEffect(() => {
    pageChanged(0);
    if (refetch) {
      refetch();
    }
  }, [filter, refetch, pageChanged]);

  const changePager = (page: number) => {
    pageChanged(page);
  };
  const changePagerSize = (e: any) => {
    pageSizeChanged(e);
  };

  return (
    <Layout>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Audit logs</Typography>
      </Box>
      <Box mt={3}>
        <TableContainer component={Paper}>
          <AuditLogFilter onFilterChanged={(filter) => setFilter(filter)} />
          <Divider />
          <Table aria-label="Audit logs table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'createdAt'}
                    direction={sortDirection}
                    onClick={() => sort('createdAt')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>

                <TableCell>Action</TableCell>
                <TableCell>User</TableCell>

                <TableCell>Company</TableCell>
                <TableCell>Resource</TableCell>
                {/* <TableCell padding="checkbox"></TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableLoading />}
              {!loading && !error && data?.auditLogs?.items.length === 0 && (
                <NoResultsRow message="No audit logs to display" />
              )}
              {!loading && error && <NoResultsRow message="There was an error fetching audit logs." />}
              {!loading &&
                !error &&
                pagedResult &&
                pagedResult?.items?.length > 0 &&
                pagedResult?.items.map((auditLog, i) => {
                  const resource =
                    auditLog.entityType && auditLog.entityIdentifier
                      ? `${auditLog.entityType} (${auditLog.entityIdentifier})`
                      : undefined;
                  return (
                    <TableRow key={i}>
                      <TableCell>{dayjs(auditLog.createdAt).format('D/MM/YYYY HH:mm')}</TableCell>
                      <TableCell>{getActionName(auditLog.action as AuditLogActions)}</TableCell>
                      <TableCell>{auditLog.userFullname}</TableCell>
                      <TableCell>{auditLog.companyName}</TableCell>
                      <TableCell>{resource}</TableCell>
                      {/* <TableCell>
                      <Tooltip title={'Show in context'}>
                        <IconButton aria-controls="simple-menu" aria-haspopup="true" onClick={onShowContext(auditLog)}>
                          <AdjustIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell> */}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={pagedResult?.total ?? 0}
            rowsPerPage={pageSize}
            page={page}
            onChangePage={(_, newPage) => changePager(newPage)}
            onChangeRowsPerPage={changePagerSize}
          />
        </TableContainer>
      </Box>
    </Layout>
  );
}
